// StepSix.js (patched by Gemini-Architect)
// Cambios clave:
// - Manejo robusto de IDs (id vs _id) para borrar y agregar.
// - Protección cuando los arrays de Formik vienen undefined.
// - Normalización de archivos que vienen del backend para que todos tengan .id.
// - Carga tolerante a errores: ignora IDs huérfanos ("VirtualScene no encontrada") sin romper el render.

import React, { useEffect, useState } from "react";
import { Form, Button, Image, Icon } from "semantic-ui-react";
import { useFormik } from "formik";
import { initialValuesSix, validationSchemaSix } from "./StepSix.form";
import { SceneFormFile } from "./SceneFormFile";
import { VirtualScenes } from "../../../../api";
import { useAuth } from "../../../../hooks";
import "./SceneForm.scss";

const virtualSceneController = new VirtualScenes();

// Helper: asegura que cualquier documento tenga .id además de _id
const normalizeFile = (f = {}) => ({
  ...f,
  id: f?.id ?? f?._id ?? null,
  title: f?.title ?? f?.name ?? "",
  url: f?.url ?? f?.location ?? f?.path ?? "",
});

// Helper: resuelve lista de IDs devolviendo solo los que existen
const resolveFilesByIds = async (ids = []) => {
  const results = await Promise.allSettled(
    ids.map((id) => virtualSceneController.getFileScene(id)),
  );

  const okFiles = [];
  const okIds = [];
  const badIds = [];

  results.forEach((r, i) => {
    if (r.status === "fulfilled" && r.value) {
      okFiles.push(normalizeFile(r.value));
      okIds.push(ids[i]);
    } else {
      badIds.push(ids[i]);
    }
  });

  return { okFiles, okIds, badIds };
};

export function StepSix({
  formData,
  updateFormData,
  prevStep,
  isUpdateMode,
  scene,
  onReload,
  onClose,
}) {
  const { accessToken } = useAuth();

  // UI state
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [isPublic, setIsPublic] = useState(true);
  const [fileFieldName, setFileFieldName] = useState("");

  // Data state
  const [moduleData, setModuleData] = useState(null);
  const [uploadedFileAdditionalMaterials, setUploadedFilesAdditionalMaterials] =
    useState({
      ModSixAdditionalMaterials: isUpdateMode
        ? moduleData?.ModSixAdditionalMaterials || []
        : formData.ModSixAdditionalMaterials || [],
    });
  const [uploadedFilesPdf, setUploadedFilesPdf] = useState({
    ModSixFilesPdf: isUpdateMode
      ? moduleData?.ModSixFilesPdf || []
      : formData.ModSixFilesPdf || [],
  });
  const [uploadedFilerepository, setUploadedFilesrepository] = useState({
    ModSixRepository: isUpdateMode
      ? moduleData?.ModSixRepository || null
      : formData.ModSixRepository || null,
  });
  const [uploadedFilefooter, setUploadedfooter] = useState({
    ModSixFooter: isUpdateMode
      ? moduleData?.ModSixFooter || null
      : formData.ModSixFooter || null,
  });

  // 1) Cargar datos del módulo en modo update
  useEffect(() => {
    async function fetchData() {
      if (!isUpdateMode) return;
      try {
        const data = await virtualSceneController.getVirtualScenesModeule(
          scene.refId,
          accessToken,
          "module_06",
        );
        setModuleData(data.moduleData);
      } catch (error) {
        console.error("Error fetching module data:", error);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateMode, accessToken, scene?.refId]);

  // 2) Con moduleData disponible, resolver archivos tolerando IDs inexistentes
  useEffect(() => {
    if (!moduleData) return;

    // Additional Materials
    if (Array.isArray(moduleData.ModSixAdditionalMaterials)) {
      (async () => {
        try {
          const { okFiles, okIds, badIds } = await resolveFilesByIds(
            moduleData.ModSixAdditionalMaterials,
          );
          setUploadedFilesAdditionalMaterials((prev) => ({
            ...prev,
            ModSixAdditionalMaterials: okFiles,
          }));
          formik.setFieldValue("ModSixAdditionalMaterials", okIds);
          if (badIds.length)
            console.warn("AdditionalMaterials inexistentes:", badIds);
        } catch (error) {
          console.error("Error al obtener AdditionalMaterials:", error);
        }
      })();
    }

    // PDFs
    if (Array.isArray(moduleData.ModSixFilesPdf)) {
      (async () => {
        try {
          const { okFiles, okIds, badIds } = await resolveFilesByIds(
            moduleData.ModSixFilesPdf,
          );
          setUploadedFilesPdf((prev) => ({
            ...prev,
            ModSixFilesPdf: okFiles,
          }));
          formik.setFieldValue("ModSixFilesPdf", okIds);
          if (badIds.length) console.warn("PDFs inexistentes:", badIds);
        } catch (error) {
          console.error("Error al obtener FilesPdf:", error);
        }
      })();
    }

    // Repository
    if (moduleData.ModSixRepository) {
      (async () => {
        try {
          const fileData = normalizeFile(
            await virtualSceneController.getFileScene(
              moduleData.ModSixRepository,
            ),
          );
          setUploadedFilesrepository((prev) => ({
            ...prev,
            ModSixRepository: fileData,
          }));
        } catch (error) {
          console.warn(
            "Repository no encontrado, limpiando valor:",
            moduleData.ModSixRepository,
          );
          setUploadedFilesrepository((prev) => ({
            ...prev,
            ModSixRepository: null,
          }));
          formik.setFieldValue("ModSixRepository", null);
        }
      })();
    }

    // Footer
    if (moduleData.ModSixFooter) {
      (async () => {
        try {
          const fileData = normalizeFile(
            await virtualSceneController.getFileScene(moduleData.ModSixFooter),
          );
          setUploadedfooter((prev) => ({ ...prev, ModSixFooter: fileData }));
        } catch (error) {
          console.warn(
            "Footer no encontrado, limpiando valor:",
            moduleData.ModSixFooter,
          );
          setUploadedfooter((prev) => ({ ...prev, ModSixFooter: null }));
          formik.setFieldValue("ModSixFooter", null);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleData]);

  // 3) Handlers de subir/borrar
  const handleFileSubmit = (fileData) => {
    const id = fileData?.id ?? fileData?._id;
    if (!id) {
      console.error("handleFileSubmit: el backend no devolvió ID", fileData);
      return;
    }
    const nf = normalizeFile(fileData);
    const safeArr = (v) => (Array.isArray(v) ? v : []);

    switch (fileFieldName) {
      case "ModSixAdditionalMaterials":
        setUploadedFilesAdditionalMaterials((prev) => ({
          ...prev,
          [fileFieldName]: [...(prev[fileFieldName] || []), nf],
        }));
        formik.setFieldValue(fileFieldName, [
          ...safeArr(formik.values[fileFieldName]),
          id,
        ]);
        break;
      case "ModSixFilesPdf":
        setUploadedFilesPdf((prev) => ({
          ...prev,
          [fileFieldName]: [...(prev[fileFieldName] || []), nf],
        }));
        formik.setFieldValue(fileFieldName, [
          ...safeArr(formik.values[fileFieldName]),
          id,
        ]);
        break;
      case "ModSixRepository":
        setUploadedFilesrepository((prev) => ({
          ...prev,
          [fileFieldName]: nf,
        }));
        formik.setFieldValue(fileFieldName, id);
        break;
      case "ModSixFooter":
        setUploadedfooter((prev) => ({ ...prev, [fileFieldName]: nf }));
        formik.setFieldValue(fileFieldName, id);
        break;
      default:
        console.error(`Campo no reconocido: ${fileFieldName}`);
        break;
    }
    setIsFileModalOpen(false);
  };

  const handleRemoveFile = async (fieldName, index) => {
    let fileObj = null;
    if (fieldName === "ModSixAdditionalMaterials") {
      fileObj =
        uploadedFileAdditionalMaterials[fieldName]?.[index ?? 0] || null;
    } else if (fieldName === "ModSixFilesPdf") {
      fileObj = uploadedFilesPdf[fieldName]?.[index ?? 0] || null;
    } else if (fieldName === "ModSixRepository") {
      fileObj = uploadedFilerepository[fieldName] || null;
    } else if (fieldName === "ModSixFooter") {
      fileObj = uploadedFilefooter[fieldName] || null;
    }

    const fileId = fileObj?.id ?? fileObj?._id;
    if (!fileId) {
      console.error(`No hay ID para borrar en ${fieldName}. Objeto:`, fileObj);
      return; // evita DELETE /image/undefined
    }

    try {
      await virtualSceneController.deleteImageRepo(accessToken, fileId);

      if (fieldName === "ModSixAdditionalMaterials") {
        setUploadedFilesAdditionalMaterials((prev) => ({
          ...prev,
          [fieldName]: (prev[fieldName] || []).filter((_, i) => i !== index),
        }));
        formik.setFieldValue(
          fieldName,
          (Array.isArray(formik.values[fieldName])
            ? formik.values[fieldName]
            : []
          ).filter((_, i) => i !== index),
        );
      } else if (fieldName === "ModSixFilesPdf") {
        setUploadedFilesPdf((prev) => ({
          ...prev,
          [fieldName]: (prev[fieldName] || []).filter((_, i) => i !== index),
        }));
        formik.setFieldValue(
          fieldName,
          (Array.isArray(formik.values[fieldName])
            ? formik.values[fieldName]
            : []
          ).filter((_, i) => i !== index),
        );
      } else if (fieldName === "ModSixRepository") {
        setUploadedFilesrepository((prev) => ({ ...prev, [fieldName]: null }));
        formik.setFieldValue(fieldName, null);
      } else if (fieldName === "ModSixFooter") {
        setUploadedfooter((prev) => ({ ...prev, [fieldName]: null }));
        formik.setFieldValue(fieldName, null);
      }
    } catch (error) {
      console.error("Error al eliminar el archivo:", error);
    }
  };

  // 4) Formik
  const formik = useFormik({
    initialValues: isUpdateMode
      ? initialValuesSix(moduleData)
      : initialValuesSix(formData),
    validationSchema: validationSchemaSix(),
    validateOnChange: false,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        let _id = "";
        if (isUpdateMode) {
          _id = scene.refId;
        } else {
          _id = localStorage.getItem("idScene");
        }

        if (values.tagsInput) {
          values.tags = [...values.tags, values.tagsInput.trim()];
          values.tagsInput = "";
        }

        const modulePath = "module_06";
        const response = await virtualSceneController.createModuleAdd(
          modulePath,
          accessToken,
          _id,
          values,
        );
        console.log("response", response);
      } catch (error) {
        console.error("Error al enviar el formulario:", error);
      }

      updateFormData({
        ...values,
        ...uploadedFileAdditionalMaterials,
        ...uploadedFilesPdf,
        ...uploadedFilerepository,
        ...uploadedFilefooter,
      });
      onReload();
      if (onClose) onClose();
    },
  });

  // 5) Render
  return (
    <Form className="virtual-scene-form" onSubmit={formik.handleSubmit}>
      {/* Imágenes Adicionales */}
      <div className="container">
        <Button
          type="button"
          onClick={() => {
            setIsPublic(true);
            setFileFieldName("ModSixAdditionalMaterials");
            setIsFileModalOpen(true);
          }}
        >
          Subir Imágenes Adicionales
        </Button>
        <div className="images-preview">
          {(
            uploadedFileAdditionalMaterials.ModSixAdditionalMaterials || []
          ).map((file, index) => (
            <div key={index} className="image-item">
              <Image src={file.url} size="small" />
              <Icon
                name="close"
                onClick={() =>
                  handleRemoveFile("ModSixAdditionalMaterials", index)
                }
              />
            </div>
          ))}
        </div>
      </div>

      {/* Fuentes */}
      <div className="container">
        <Form.TextArea
          name="ModSixFuentes"
          placeholder="fuentes del recurso"
          onChange={formik.handleChange}
          value={formik.values.ModSixFuentes}
          error={
            formik.errors.ModSixFuentes
              ? { content: formik.errors.ModSixFuentes }
              : null
          }
        />
      </div>

      {/* PDF Upload */}
      <div className="container">
        <Button
          type="button"
          onClick={() => {
            setIsPublic(false);
            setFileFieldName("ModSixFilesPdf");
            setIsFileModalOpen(true);
          }}
        >
          Subir PDFs
        </Button>
        <div className="container">
          {(uploadedFilesPdf.ModSixFilesPdf || []).map((file, index) => (
            <div key={index} className="file-item">
              <p>{file.title}</p>
              <Icon
                name="close"
                onClick={() => handleRemoveFile("ModSixFilesPdf", index)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Repository */}
      <div className="container">
        <Button
          type="button"
          onClick={() => {
            setIsPublic(false);
            setFileFieldName("ModSixRepository");
            setIsFileModalOpen(true);
          }}
        >
          Subir Repositorio
        </Button>
        {uploadedFilerepository.ModSixRepository && (
          <div className="file-preview">
            <p>{uploadedFilerepository.ModSixRepository.title}</p>
            <Icon
              name="close"
              onClick={() => handleRemoveFile("ModSixRepository")}
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="container">
        <Button
          type="button"
          onClick={() => {
            setIsPublic(true);
            setFileFieldName("ModSixFooter");
            setIsFileModalOpen(true);
          }}
        >
          Subir Imagen de cierre
        </Button>
        {uploadedFilefooter.ModSixFooter && (
          <div className="container">
            <Image
              src={uploadedFilefooter.ModSixFooter.url}
              size="small"
              centered
            />
            <Icon
              name="close"
              onClick={() => handleRemoveFile("ModSixFooter")}
            />
          </div>
        )}
      </div>

      {/* Modal de subida */}
      <SceneFormFile
        isOpen={isFileModalOpen}
        onClose={() => setIsFileModalOpen(false)}
        onSubmit={handleFileSubmit}
        isPublic={isPublic}
        idScene={formik.values.idScene}
      />

      <div className="formgroup">
        <Form.Group>
          <Button
            type="button"
            onClick={prevStep}
            color="orange"
            style={{ width: "33.3333%" }}
          >
            Anterior
          </Button>
          <Button
            type="submit"
            primary
            style={{ width: "66.6666%" }}
            loading={formik.isSubmitting}
            onClick={() => console.log(formik.values, isUpdateMode)}
          >
            Publicar y cerrar
          </Button>
        </Form.Group>
      </div>
    </Form>
  );
}
